import * as THREE from 'three';
import { DRIFT_PITCH, DRIFT_SPEED, DRIFT_YAW } from '../../constants';
import { distanceToFrameBounds, normalizeFrameInsets } from '../../utils/neuralSceneUtils';

const driftSpherical = new THREE.Spherical();
const driftOffset = new THREE.Vector3();

/**
 * Closed form of the idle orbit. t=0 is the rest pose.
 * theta = θ0 + sin(t)·yaw,  phi = φ0 + (1−cos(t))·pitch
 */
export function idleOrbitOffset(homeOffset, t, yawAmp, pitchAmp, out = driftOffset) {
  driftSpherical.setFromVector3(homeOffset);
  driftSpherical.theta += Math.sin(t) * yawAmp;
  driftSpherical.phi += (1 - Math.cos(t)) * pitchAmp;
  out.setFromSpherical(driftSpherical);
  return out;
}

const _orbitCam = new THREE.PerspectiveCamera();
const _orbitHome = new THREE.Vector3();
const _orbitOff = new THREE.Vector3();
const _orbitPt = new THREE.Vector3();
const _orbitNdc = new THREE.Vector3();

function boundsCorners(bounds) {
  const midX = ((bounds.minX ?? -6) + (bounds.maxX ?? 6)) * 0.5;
  const midY = ((bounds.minY ?? -4) + (bounds.maxY ?? 4)) * 0.5;
  return [
    [bounds.minX, bounds.minY],
    [bounds.maxX, bounds.minY],
    [bounds.minX, bounds.maxY],
    [bounds.maxX, bounds.maxY],
    [midX, bounds.minY],
    [midX, bounds.maxY],
    [bounds.minX, midY],
    [bounds.maxX, midY],
  ];
}

/**
 * Pull back from a tight center-fit so every idle-orbit pose still holds
 * the content box. Rest pose stays on the box center — extra air is even.
 */
export function distanceToContainIdleOrbit({
  bounds,
  fovDeg,
  aspect,
  viewDir,
  target,
  insets = 0,
  yaw = DRIFT_YAW,
  pitch = DRIFT_PITCH,
  padding = 1,
  samples = 10,
}) {
  const rest = distanceToFrameBounds(bounds, fovDeg, aspect, padding, insets);
  const { top } = normalizeFrameInsets(insets);
  const minX = -0.98;
  const maxX = 0.98;
  const minY = -0.98;
  const maxY = 1 - 2 * top - 0.02;
  const corners = boundsCorners(bounds);
  _orbitCam.fov = fovDeg;
  _orbitCam.aspect = Math.max(aspect, 0.2);
  _orbitCam.near = 0.1;
  _orbitCam.far = 500;
  _orbitCam.updateProjectionMatrix();
  const homeDir = viewDir.clone().normalize();

  const fits = (dist) => {
    _orbitHome.copy(homeDir).multiplyScalar(dist);
    for (let i = 0; i < samples; i += 1) {
      idleOrbitOffset(_orbitHome, (i / samples) * Math.PI * 2, yaw, pitch, _orbitOff);
      _orbitCam.position.copy(target).add(_orbitOff);
      _orbitCam.lookAt(target);
      _orbitCam.updateMatrixWorld(true);
      for (let c = 0; c < corners.length; c += 1) {
        _orbitPt.set(corners[c][0], corners[c][1], 0);
        _orbitNdc.copy(_orbitPt).project(_orbitCam);
        if (_orbitNdc.z < -1 || _orbitNdc.z > 1) return false;
        if (_orbitNdc.x < minX || _orbitNdc.x > maxX) return false;
        if (_orbitNdc.y < minY || _orbitNdc.y > maxY) return false;
      }
    }
    return true;
  };

  if (fits(rest)) return rest;
  let lo = rest;
  let hi = rest;
  for (let i = 0; i < 8 && !fits(hi); i += 1) hi *= 1.2;
  if (!fits(hi)) return hi;
  for (let i = 0; i < 12; i += 1) {
    const mid = (lo + hi) * 0.5;
    if (fits(mid)) hi = mid;
    else lo = mid;
  }
  return hi;
}

/** Smoothstep 0→1 so the orbit eases in instead of popping to speed. */
export function idleEaseGain(elapsedSec, durationSec) {
  const t = THREE.MathUtils.clamp(durationSec > 0 ? elapsedSec / durationSec : 1, 0, 1);
  return t * t * (3 - 2 * t);
}

/** Slow circular orbit from the current camera. `gain` eases 0→1 after a drag. */
export function applyIdleDrift(camera, controls, driftRef, delta, gain = 1) {
  driftOffset.copy(camera.position).sub(controls.target);
  driftSpherical.setFromVector3(driftOffset);
  if (driftRef.fresh) {
    const yawRoom = Math.min(
      driftSpherical.theta - controls.minAzimuthAngle,
      controls.maxAzimuthAngle - driftSpherical.theta,
    );
    const pitchRoom = Math.min(
      driftSpherical.phi - controls.minPolarAngle,
      controls.maxPolarAngle - driftSpherical.phi,
    );
    driftRef.t = 0;
    driftRef.yaw = Math.min(DRIFT_YAW, Math.max(0.03, yawRoom * 0.8));
    driftRef.pitch = Math.min(DRIFT_PITCH, Math.max(0.02, pitchRoom * 0.8));
    driftRef.fresh = false;
  }
  const prevT = driftRef.t;
  driftRef.t += delta * DRIFT_SPEED * THREE.MathUtils.clamp(gain, 0, 1);
  driftSpherical.theta += (Math.sin(driftRef.t) - Math.sin(prevT)) * driftRef.yaw;
  driftSpherical.phi += ((1 - Math.cos(driftRef.t)) - (1 - Math.cos(prevT))) * driftRef.pitch;
  driftSpherical.theta = THREE.MathUtils.clamp(
    driftSpherical.theta,
    controls.minAzimuthAngle,
    controls.maxAzimuthAngle,
  );
  driftSpherical.phi = THREE.MathUtils.clamp(
    driftSpherical.phi,
    controls.minPolarAngle,
    controls.maxPolarAngle,
  );
  driftOffset.setFromSpherical(driftSpherical);
  camera.position.copy(controls.target).add(driftOffset);
  camera.lookAt(controls.target);
}
